/**
 * Lifecycle marks of a shift in the planner (#2038): the "changed since publication" and
 * "confirmed" glyphs on a block, the tooltip text with the timestamps, and the legend
 * under the grid. The mobile app uses the same two glyphs (pencil, check).
 */
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { ArrowLeftRight, CheckCircle2, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import type { Shift, ShiftStatusType } from '@/types/shifts';
import { ShiftStatus } from '@/types/shifts';
import { SHIFT_STATUS_BLOCK_CLASSES } from './ShiftStatusBadge';

/** True when a visible shift changed after it was published (the employee got a push). */
export function isChangedAfterPublish(shift: Pick<Shift, 'status' | 'changed_after_publish_at'>): boolean {
  return (
    !!shift.changed_after_publish_at &&
    (shift.status === ShiftStatus.PUBLISHED || shift.status === ShiftStatus.CONFIRMED)
  );
}

/** Tooltip text of a block: one line per timestamp the shift carries. */
export function shiftLifecycleTitle(shift: Shift, i18n: I18n): string {
  const lines: string[] = [];
  if (shift.published_at) {
    lines.push(
      i18n._({
        id: 'shifts.lifecycle.publishedAt',
        message: 'Published on {date}',
        values: { date: formatDateTime(shift.published_at, i18n.locale) },
      })
    );
  }
  if (shift.status === ShiftStatus.CONFIRMED && shift.confirmed_at) {
    lines.push(
      i18n._({
        id: 'shifts.lifecycle.confirmedAt',
        message: 'Confirmed on {date}',
        values: { date: formatDateTime(shift.confirmed_at, i18n.locale) },
      })
    );
  }
  if (isChangedAfterPublish(shift) && shift.changed_after_publish_at) {
    lines.push(
      i18n._({
        id: 'shifts.lifecycle.changedAt',
        message: 'Changed on {date}',
        values: { date: formatDateTime(shift.changed_after_publish_at, i18n.locale) },
      })
    );
  }
  return lines.join('\n');
}

/** The changed and confirmed glyphs of a block; each carries its own screen-reader label. */
export function ShiftLifecycleGlyphs({ shift, className }: { shift: Shift; className?: string }) {
  const { i18n } = useLingui();
  const changed = isChangedAfterPublish(shift);
  const confirmed = shift.status === ShiftStatus.CONFIRMED;
  if (!changed && !confirmed) return null;
  return (
    <span className={cn('inline-flex flex-shrink-0 items-center gap-0.5', className)}>
      {changed && (
        <PenLine
          className="h-3 w-3"
          role="img"
          aria-label={i18n._({ id: 'shifts.lifecycle.changed', message: 'Changed since publication' })}
          data-testid="shift-changed-glyph"
        />
      )}
      {confirmed && (
        <CheckCircle2
          className="h-3 w-3"
          role="img"
          aria-label={i18n._({ id: 'shifts.status.confirmed', message: 'Bestätigt' })}
          data-testid="shift-confirmed-glyph"
        />
      )}
    </span>
  );
}

/** Classes of a block: the status tone, plus the hatch for a draft. */
export function shiftBlockClasses(status: string | undefined): string {
  return cn(
    SHIFT_STATUS_BLOCK_CLASSES[status as ShiftStatusType],
    status === ShiftStatus.DRAFT && 'shift-draft-hatch'
  );
}

/**
 * Legend row under the planner grid. `hint` names what a click and a drag do in the
 * visible view (#2038 UX pass); it is left out of the print.
 */
export function ShiftLegend({ className, hint }: { className?: string; hint?: string }) {
  const { i18n } = useLingui();
  const swatch = 'inline-block h-3 w-5 rounded-sm border';
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-x-6 gap-y-1', className)}>
      <ul
        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"
        aria-label={i18n._({ id: 'shifts.legend.label', message: 'Legend' })}
        data-testid="shift-legend"
      >
        <li className="inline-flex items-center gap-1.5">
          <span className={cn(swatch, shiftBlockClasses(ShiftStatus.DRAFT))} aria-hidden="true" />
          {i18n._({ id: 'shifts.status.draft', message: 'Entwurf' })}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className={cn(swatch, shiftBlockClasses(ShiftStatus.PUBLISHED))} aria-hidden="true" />
          {i18n._({ id: 'shifts.status.published', message: 'Veröffentlicht' })}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-success-on-surface" aria-hidden="true" />
          {i18n._({ id: 'shifts.status.confirmed', message: 'Bestätigt' })}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
          {i18n._({ id: 'shifts.lifecycle.changed', message: 'Changed since publication' })}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className={cn(swatch, shiftBlockClasses(ShiftStatus.CANCELLED))} aria-hidden="true" />
          {i18n._({ id: 'shifts.status.cancelled', message: 'Storniert' })}
        </li>
        <li className="inline-flex items-center gap-1.5" data-testid="legend-open">
          <span className={cn(swatch, 'border-2 border-dotted border-foreground/50')} aria-hidden="true" />
          {i18n._({ id: 'shifts.open.legend', message: 'Open: nobody assigned, employees can ask for it' })}
        </li>
        <li className="inline-flex items-center gap-1.5" data-testid="legend-swap">
          <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
          {i18n._({ id: 'shifts.swap.legend', message: 'Swap offered' })}
        </li>
      </ul>
      {hint && (
        <p className="text-xs text-muted-foreground print:hidden" data-testid="shift-grid-hint">
          {hint}
        </p>
      )}
    </div>
  );
}
